import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ONBOARDING_CHANNELS,
  ONBOARDING_GOALS,
  ONBOARDING_PLATFORMS,
  ONBOARDING_STEPS,
  ONBOARDING_TEAM_SIZES,
  type OnboardingChannel,
  type OnboardingStep,
} from '@prakash-software/shared';
import {
  checkInstall,
  fetchOnboarding,
  loadWidgetAppearance,
  saveProfile,
  saveStep,
  saveWidgetAppearance,
  sendTestMessage,
  StaleOnboardingError,
  type OnboardingState,
} from '../lib/onboarding-api';
import {
  CHANNEL_LABEL,
  GOAL_LABEL,
  INSTALL_HINT,
  PLATFORM_LABEL,
  STEP_LABEL,
  TEAM_SIZE_LABEL,
} from '../lib/onboarding-labels';
import { isApiError } from '../lib/api';
import { serverOrigin } from '../lib/server-origin';
import { Checklist } from '../components/SetupChecklist';
import styles from './Onboarding.module.css';

// The self-serve setup wizard. Every step saves on its own and can be skipped;
// the dashboard is reachable throughout (this page lives inside the Shell).
// The server holds the progress, so the wizard resumes wherever it was left —
// on another device too — and a save based on stale state comes back 409 with
// the newer state, which replaces what this tab shows.

type View = OnboardingStep | 'finish';

const stepAfter = (step: OnboardingStep): View => ONBOARDING_STEPS[ONBOARDING_STEPS.indexOf(step) + 1] ?? 'finish';
const stepBefore = (step: View): OnboardingStep | null =>
  step === 'finish' ? ONBOARDING_STEPS[ONBOARDING_STEPS.length - 1]! : (ONBOARDING_STEPS[ONBOARDING_STEPS.indexOf(step) - 1] ?? null);

export function Onboarding() {
  const [state, setState] = useState<OnboardingState | null | undefined>(undefined);
  const [view, setView] = useState<View>('profile');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ kind: 'error' | 'notice'; text: string } | null>(null);

  useEffect(() => {
    let live = true;
    fetchOnboarding()
      .then((s) => {
        if (!live) return;
        setState(s);
        if (s) setView(s.nextStep ?? 'finish');
      })
      .catch(() => live && setMessage({ kind: 'error', text: 'Could not load your setup. Refresh to try again.' }));
    return () => {
      live = false;
    };
  }, []);

  /** Run one save; move on when it lands, adopt the newer state when stale. */
  const run = useCallback(async (from: OnboardingStep, save: () => Promise<OnboardingState>) => {
    setBusy(true);
    setMessage(null);
    try {
      setState(await save());
      setView(stepAfter(from));
    } catch (err) {
      if (err instanceof StaleOnboardingError) {
        setState(err.current);
        setMessage({ kind: 'notice', text: 'Your setup was changed somewhere else, so this page now shows the latest version. Check it and save again.' });
      } else if (isApiError(err) && err.status === 400) {
        setMessage({ kind: 'error', text: 'Some of that could not be saved. Check the highlighted answers.' });
      } else if (isApiError(err) && err.status === 403) {
        setMessage({ kind: 'error', text: 'Only an admin of this workspace can change its setup.' });
      } else {
        setMessage({ kind: 'error', text: 'Could not save. Check your connection and try again.' });
      }
    } finally {
      setBusy(false);
    }
  }, []);

  if (state === null) return <Navigate to="/dashboard" replace />;
  if (state === undefined) {
    return (
      <div className={styles.page}>
        {message ? <p className={styles.error} role="alert">{message.text}</p> : <p role="status">Loading your setup…</p>}
      </div>
    );
  }

  const skip = (step: OnboardingStep) => run(step, () => saveStep(step, 'skipped', state.version));
  const nav = { busy: busy || !state.canEdit, onSkip: skip, onBack: () => setView(stepBefore(view) ?? view) };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1>Set up Openline</h1>
          <p>A few questions, then your chat widget. Skip anything and come back later.</p>
        </div>
        <Link to="/dashboard">Go to the dashboard →</Link>
      </header>

      <nav aria-label="Setup steps">
        <ol className={styles.steps}>
          {ONBOARDING_STEPS.map((step, i) => {
            const status = state.steps[step]?.status;
            return (
              <li key={step}>
                <button
                  type="button"
                  className={styles.stepBtn}
                  aria-current={view === step ? 'step' : undefined}
                  onClick={() => setView(step)}
                >
                  <span className={styles.mark} data-status={status ?? 'pending'} aria-hidden>
                    {status === 'completed' ? '✓' : i + 1}
                  </span>
                  <span>{STEP_LABEL[step]}</span>
                  {status === 'skipped' && <span className={styles.stepMeta}>Skipped</span>}
                </button>
              </li>
            );
          })}
          <li>
            <button type="button" className={styles.stepBtn} aria-current={view === 'finish' ? 'step' : undefined} onClick={() => setView('finish')}>
              <span className={styles.mark} data-status={state.activatedAt ? 'completed' : 'pending'} aria-hidden>
                {state.activatedAt ? '✓' : '★'}
              </span>
              <span>Test &amp; go live</span>
            </button>
          </li>
        </ol>
      </nav>

      <section className={styles.panel} aria-live="polite">
        {!state.canEdit && (
          <p className={styles.notice}>Only an admin of this workspace can change its setup. You can look around.</p>
        )}
        {message && (
          <p className={message.kind === 'error' ? styles.error : styles.notice} role={message.kind === 'error' ? 'alert' : 'status'}>
            {message.text}
          </p>
        )}
        {view === 'profile' && (
          <ProfileStep key={state.version} state={state} {...nav} onSave={(p) => run('profile', () => saveProfile(p, state.version))} />
        )}
        {view === 'goal' && (
          <ChoiceStep
            key={state.version}
            step="goal"
            title="What do you want Openline to do first?"
            options={ONBOARDING_GOALS}
            labels={GOAL_LABEL}
            initial={state.answers.goal}
            {...nav}
            onSave={(v) => run('goal', () => saveStep('goal', 'completed', state.version, v))}
          />
        )}
        {view === 'team_size' && (
          <ChoiceStep
            key={state.version}
            step="team_size"
            title="How many people will answer chats?"
            options={ONBOARDING_TEAM_SIZES}
            labels={TEAM_SIZE_LABEL}
            initial={state.answers.teamSize}
            {...nav}
            onSave={(v) => run('team_size', () => saveStep('team_size', 'completed', state.version, v))}
          />
        )}
        {view === 'platform' && (
          <ChoiceStep
            key={state.version}
            step="platform"
            title="What is your website built with?"
            options={ONBOARDING_PLATFORMS}
            labels={PLATFORM_LABEL}
            initial={state.answers.platform}
            {...nav}
            onSave={(v) => run('platform', () => saveStep('platform', 'completed', state.version, v))}
          />
        )}
        {view === 'channels' && (
          <ChannelsStep
            key={state.version}
            initial={state.answers.channels}
            {...nav}
            onSave={(v) => run('channels', () => saveStep('channels', 'completed', state.version, v))}
          />
        )}
        {view === 'widget' && state.site && (
          <WidgetStep
            key={state.version}
            siteId={state.site.id}
            {...nav}
            onSave={(appearance) =>
              run('widget', async () => {
                await saveWidgetAppearance(state.site!.id, appearance);
                return saveStep('widget', 'completed', state.version);
              })
            }
          />
        )}
        {view === 'install' && (
          <InstallStep
            key={state.version}
            state={state}
            {...nav}
            onDone={() => run('install', () => saveStep('install', 'completed', state.version))}
            onGoToProfile={() => setView('profile')}
          />
        )}
        {view === 'finish' && <FinishStep state={state} onRefresh={setState} />}
      </section>
    </div>
  );
}

// ─── shared step chrome ───

interface NavProps {
  busy: boolean;
  onSkip: (step: OnboardingStep) => void;
  onBack: () => void;
}

function Actions({
  step,
  busy,
  onSkip,
  onBack,
  canSave = true,
  saveLabel = 'Save and continue',
  skipLabel = 'Skip for now',
  extra,
}: NavProps & { step: OnboardingStep; canSave?: boolean; saveLabel?: string; skipLabel?: string; extra?: ReactNode }) {
  return (
    <div className={styles.actions}>
      {step !== 'profile' && (
        <button type="button" className={styles.ghost} onClick={onBack}>
          ← Back
        </button>
      )}
      <span className={styles.spacer} />
      {extra}
      <button type="button" className={styles.secondary} onClick={() => onSkip(step)} disabled={busy}>
        {skipLabel}
      </button>
      <button type="submit" className={styles.primary} disabled={busy || !canSave}>
        {saveLabel}
      </button>
    </div>
  );
}

// ─── steps ───

function ProfileStep({
  state,
  onSave,
  ...nav
}: NavProps & {
  state: OnboardingState;
  onSave: (p: { workspaceName: string; website: string | null; industry: string | null }) => void;
}) {
  const [workspaceName, setWorkspaceName] = useState(state.profile.workspaceName);
  const [website, setWebsite] = useState(state.profile.website ?? state.site?.origin ?? '');
  const [industry, setIndustry] = useState(state.profile.industry ?? '');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSave({ workspaceName: workspaceName.trim(), website: website.trim() || null, industry: industry.trim() || null });
  };
  return (
    <form onSubmit={submit} className={styles.panel} style={{ padding: 0, border: 0, boxShadow: 'none' }}>
      <h2>Tell us about your business</h2>
      <label className={styles.field}>
        <span className={styles.label}>Business or workspace name</span>
        <input className={styles.input} required maxLength={200} value={workspaceName} onChange={(e) => setWorkspaceName(e.target.value)} />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Website</span>
        <input className={styles.input} inputMode="url" placeholder="example.com" maxLength={300} value={website} onChange={(e) => setWebsite(e.target.value)} />
        <span className={styles.hint}>The chat widget only runs on this address. Leave it blank if you don&rsquo;t have a site yet.</span>
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Industry (optional)</span>
        <input className={styles.input} maxLength={100} placeholder="e.g. Online retail" value={industry} onChange={(e) => setIndustry(e.target.value)} />
      </label>
      <Actions step="profile" {...nav} canSave={!!workspaceName.trim()} />
    </form>
  );
}

function ChoiceStep<T extends string>({
  step,
  title,
  options,
  labels,
  initial,
  onSave,
  ...nav
}: NavProps & {
  step: OnboardingStep;
  title: string;
  options: readonly T[];
  labels: Record<T, string>;
  initial: T | null;
  onSave: (value: T) => void;
}) {
  const [value, setValue] = useState<T | null>(initial);
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (value) onSave(value);
      }}
      className={styles.panel}
      style={{ padding: 0, border: 0, boxShadow: 'none' }}
    >
      <fieldset className={styles.choices}>
        <legend>
          <h2>{title}</h2>
        </legend>
        {options.map((o) => (
          <label key={o} className={styles.choice}>
            <input type="radio" name={step} value={o} checked={value === o} onChange={() => setValue(o)} />
            {labels[o]}
          </label>
        ))}
      </fieldset>
      <Actions step={step} {...nav} canSave={!!value} />
    </form>
  );
}

function ChannelsStep({ initial, onSave, ...nav }: NavProps & { initial: OnboardingChannel[]; onSave: (v: OnboardingChannel[]) => void }) {
  const [picked, setPicked] = useState<Set<OnboardingChannel>>(new Set(initial.length ? initial : ['web']));
  const toggle = (c: OnboardingChannel) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(c)) n.delete(c);
      else n.add(c);
      return n;
    });
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave(ONBOARDING_CHANNELS.filter((c) => picked.has(c)));
      }}
      className={styles.panel}
      style={{ padding: 0, border: 0, boxShadow: 'none' }}
    >
      <fieldset className={styles.choices}>
        <legend>
          <h2>Where do your customers reach you?</h2>
        </legend>
        {ONBOARDING_CHANNELS.map((c) => (
          <label key={c} className={styles.choice}>
            <input type="checkbox" checked={picked.has(c)} onChange={() => toggle(c)} />
            {CHANNEL_LABEL[c]}
          </label>
        ))}
      </fieldset>
      <p className={styles.hint}>This only tells us what to suggest — you connect channels later, from the site settings.</p>
      <Actions step="channels" {...nav} canSave={picked.size > 0} />
    </form>
  );
}

const HEX = /^#[0-9a-fA-F]{6}$/;

function WidgetStep({
  siteId,
  onSave,
  ...nav
}: NavProps & {
  siteId: string;
  onSave: (a: { chatTitle: string | null; greeting: string | null; accentColor: string; bubblePosition: 'bottom-right' | 'bottom-left' }) => void;
}) {
  const [loaded, setLoaded] = useState(false);
  const [chatTitle, setChatTitle] = useState('');
  const [greeting, setGreeting] = useState('');
  const [color, setColor] = useState('#F97316');
  const [position, setPosition] = useState<'bottom-right' | 'bottom-left'>('bottom-right');

  useEffect(() => {
    let live = true;
    loadWidgetAppearance(siteId)
      .then((c) => {
        if (!live) return;
        setChatTitle(c.chatTitle ?? '');
        setGreeting(c.greeting ?? '');
        if (HEX.test(c.accentColor)) setColor(c.accentColor);
        setPosition(c.bubblePosition === 'bottom-left' ? 'bottom-left' : 'bottom-right');
      })
      .catch(() => undefined)
      .finally(() => live && setLoaded(true));
    return () => {
      live = false;
    };
  }, [siteId]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave({ chatTitle: chatTitle.trim() || null, greeting: greeting.trim() || null, accentColor: color, bubblePosition: position });
      }}
      className={styles.panel}
      style={{ padding: 0, border: 0, boxShadow: 'none' }}
    >
      <h2>Make the chat widget yours</h2>
      <p>You can change all of this, and much more, later in the site&rsquo;s widget settings.</p>
      <label className={styles.field}>
        <span className={styles.label}>Chat title</span>
        <input className={styles.input} maxLength={60} placeholder="e.g. Acme Support" value={chatTitle} onChange={(e) => setChatTitle(e.target.value)} />
      </label>
      <label className={styles.field}>
        <span className={styles.label}>Greeting</span>
        <textarea className={styles.input} maxLength={280} placeholder="Hi! How can we help?" value={greeting} onChange={(e) => setGreeting(e.target.value)} />
        <span className={styles.hint}>Shown as plain text. {280 - greeting.length} characters left.</span>
      </label>
      <div className={styles.row}>
        <label className={styles.field}>
          <span className={styles.label}>Brand colour</span>
          <input type="color" className={styles.colorInput} value={color} onChange={(e) => setColor(e.target.value)} />
        </label>
        <fieldset className={styles.choices} style={{ flex: 1 }}>
          <legend>Launcher position</legend>
          {(['bottom-right', 'bottom-left'] as const).map((p) => (
            <label key={p} className={styles.choice}>
              <input type="radio" name="position" checked={position === p} onChange={() => setPosition(p)} />
              {p === 'bottom-right' ? 'Bottom right' : 'Bottom left'}
            </label>
          ))}
        </fieldset>
      </div>
      <Actions step="widget" {...nav} canSave={loaded && HEX.test(color)} skipLabel="Use the defaults" />
    </form>
  );
}

function InstallStep({
  state,
  onDone,
  onGoToProfile,
  ...nav
}: NavProps & { state: OnboardingState; onDone: () => void; onGoToProfile: () => void }) {
  const site = state.site;
  const [checking, setChecking] = useState(false);
  const [install, setInstall] = useState(site?.install ?? null);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  if (!site) return <p className={styles.error}>This workspace has no site to install yet.</p>;
  // Same tag as the site page's Integration section: the bundle derives the
  // server from its own URL, and the token is the site's public identifier.
  const snippet = `<script\n  src="${serverOrigin()}/openline.js"\n  data-site-token="${site.embedToken}"\n  async defer\n></script>`;
  const platform = state.answers.platform ?? 'custom';

  async function check() {
    setChecking(true);
    setCheckError(null);
    try {
      const r = await checkInstall();
      setInstall({ state: r.state, lastSeenAt: r.lastSeenAt, seenOrigin: r.seenOrigin, checkedAt: new Date().toISOString() });
    } catch (err) {
      setCheckError(isApiError(err) && err.status === 429 ? 'Checking too often — wait a minute.' : 'Could not check right now. Try again.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onDone();
      }}
      className={styles.panel}
      style={{ padding: 0, border: 0, boxShadow: 'none' }}
    >
      <h2>Add the widget to your website</h2>
      {!site.origin ? (
        <p className={styles.notice}>
          Add your website address first — the widget only loads on the site you name.{' '}
          <button type="button" className={styles.ghost} onClick={onGoToProfile}>
            Add my website
          </button>
        </p>
      ) : (
        <p>
          Paste this code on <strong>{site.origin}</strong>. {INSTALL_HINT[platform]}
        </p>
      )}
      <pre className={styles.snippet} aria-label="Embed code">
        {snippet}
      </pre>
      <div className={styles.row}>
        <button
          type="button"
          className={styles.secondary}
          onClick={() => {
            void navigator.clipboard?.writeText(snippet).then(() => setCopied(true));
          }}
        >
          {copied ? 'Copied' : 'Copy code'}
        </button>
        <button type="button" className={styles.secondary} onClick={() => void check()} disabled={checking || !site.origin}>
          {checking ? 'Checking…' : 'Check installation'}
        </button>
      </div>
      <p className={styles.hint}>
        The code only contains your site&rsquo;s public ID — never a password or key — so it is safe to paste into your page.
      </p>
      {checkError && <p className={styles.error} role="alert">{checkError}</p>}
      {install?.state === 'verified' && (
        <p className={styles.ok} role="status">
          Installed — the widget last loaded{install.seenOrigin ? ` on ${install.seenOrigin}` : ''}
          {install.lastSeenAt ? ` at ${new Date(install.lastSeenAt).toLocaleString()}` : ''}.
        </p>
      )}
      {install?.state === 'failed' && (
        <div className={styles.notice} role="status">
          <strong>We haven&rsquo;t seen the widget load yet.</strong> It counts as installed the first time a browser opens a page with the code on it.
          <ul>
            <li>Open your website in a new tab after publishing the change, then check again.</li>
            <li>Make sure the page is on {site.origin ?? 'the website you entered'} — the widget won&rsquo;t load anywhere else.</li>
            <li>If your site caches pages, clear the cache so visitors get the new version.</li>
          </ul>
        </div>
      )}
      <Actions
        step="install"
        {...nav}
        saveLabel="Continue"
        skipLabel={state.answers.platform === 'none' || !site.origin ? 'No website yet' : 'Do this later'}
      />
    </form>
  );
}

function FinishStep({ state, onRefresh }: { state: OnboardingState; onRefresh: (s: OnboardingState) => void }) {
  const [sending, setSending] = useState(false);
  const [testSession, setTestSession] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Activation needs a visitor, so watch for it while this panel is open.
  useEffect(() => {
    if (state.activatedAt) return;
    const t = setInterval(() => {
      void fetchOnboarding().then((s) => s && onRefresh(s)).catch(() => undefined);
    }, 20_000);
    return () => clearInterval(t);
  }, [state.activatedAt, onRefresh]);

  async function sendTest() {
    setSending(true);
    setError(null);
    try {
      setTestSession(await sendTestMessage());
      const s = await fetchOnboarding();
      if (s) onRefresh(s);
    } catch (err) {
      setError(isApiError(err) && err.status === 429 ? 'You have sent a few already — try again in a few minutes.' : 'Could not send a test message.');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className={styles.panel} style={{ padding: 0, border: 0, boxShadow: 'none' }}>
      <h2>{state.activatedAt ? 'You are live 🎉' : 'Test it, then go live'}</h2>
      <p>
        {state.activatedAt
          ? 'A real visitor has messaged you. New conversations appear in your inbox as they arrive.'
          : 'Send yourself a test message to see how a conversation looks in your inbox. Setup is complete when a real visitor writes to you from your website.'}
      </p>
      <div className={styles.row}>
        {state.canEdit && (
          <button type="button" className={styles.primary} onClick={() => void sendTest()} disabled={sending}>
            {sending ? 'Sending…' : 'Send a test message'}
          </button>
        )}
        <Link to="/dashboard">Go to the dashboard</Link>
      </div>
      {testSession && (
        <p className={styles.ok} role="status">
          Test message sent. <Link to={`/conversation/${testSession}`}>Open the conversation →</Link> It&rsquo;s marked as a test and doesn&rsquo;t count as your
          first real visitor.
        </p>
      )}
      {error && <p className={styles.error} role="alert">{error}</p>}
      <Checklist items={state.checklist} />
    </div>
  );
}
